import {
    TrustCheckResultItem,
    TrustCheckSignal,
    TrustCheckType,
} from '@trustoo/types/api/controllers/trustCheckReport'
import clsx from 'clsx'
import ReportList, { ColumnDefinition, ReportData } from 'components/reports/common/ReportList'
import {
    renderBoolean,
    renderDate,
    renderProviderId,
    renderText,
} from 'components/reports/common/reportRenderers'
import {
    CHECK_TYPES,
    checkStatusClassName,
    checkStatusLabel,
    outcomeClassName,
    outcomeLabel,
    PROVIDER_STATUSES,
} from 'components/reports/trustCheck/trustCheckLabels'
import { fetchTrustCheckReport, setSorting } from 'features/reports/trustCheckReportSlice'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import { useCallback, useMemo } from 'react'

const renderCheckCell = (item: TrustCheckResultItem, type: TrustCheckType) => {
    const status = item.check_statuses[type]
    const signals = item.signals.filter((signal) => signal.check_type === type)
    const triggers = signals.map((signal) => signal.trigger_value).join('\n')

    return (
        <div key={type} className="flex flex-col gap-1" title={triggers}>
            <span
                className={clsx(
                    'w-fit rounded px-2 py-0.5 text-sm font-bold whitespace-nowrap',
                    checkStatusClassName(status),
                )}>
                {checkStatusLabel(status)}
            </span>
            {signals.map((signal: TrustCheckSignal, index) => (
                <span key={index} className="text-sm text-grey-700">
                    {signal.trigger_value}
                </span>
            ))}
        </div>
    )
}

const TrustCheckReportList = () => {
    const dispatch = useAppDispatch()
    const { pages, rows, pagination, filters, sorting, isLoading } = useAppSelector(
        (state) => state.trustCheckReport,
    )

    const columns = useMemo(
        (): ColumnDefinition[] => [
            { key: 'service_provider_id', name: 'Provider' },
            { key: 'business_name', name: 'Business name', sortable: false },
            { key: 'country_code', name: 'Market' },
            { key: 'signup_date', name: 'Sign-up' },
            { key: 'provider_status', name: 'Current status' },
            { key: 'is_bad_actor', name: 'Bad actor', sortable: false },
            { key: 'outcome', name: 'Outcome' },
            ...CHECK_TYPES.map((type) => ({ key: type.value, name: type.name, sortable: false })),
            { key: 'high_risk_services', name: 'High-risk services', sortable: false },
        ],
        [],
    )

    const renderRow = useCallback(
        (id: string) => {
            const item = rows[id]
            if (!item) {
                return { cols: [] }
            }

            return {
                cols: [
                    renderProviderId(
                        item.service_provider_id,
                        'service_provider_id',
                        '/service/providers/provider',
                    ),
                    renderText(item.business_name, 'business_name'),
                    renderText(item.country_code, 'country_code'),
                    renderDate(item.signup_date, 'signup_date'),
                    renderText(
                        PROVIDER_STATUSES.find((option) => option.value === item.provider_status)
                            ?.name ?? item.provider_status,
                        'provider_status',
                    ),
                    renderBoolean(item.is_bad_actor, 'is_bad_actor'),
                    <span
                        key="outcome"
                        className={clsx(
                            'rounded px-2 py-0.5 text-sm font-bold whitespace-nowrap',
                            outcomeClassName(item.outcome),
                        )}>
                        {outcomeLabel(item.outcome)}
                    </span>,
                    ...CHECK_TYPES.map((type) => renderCheckCell(item, type.value)),
                    renderText(item.high_risk_services, 'high_risk_services'),
                ],
            }
        },
        [rows],
    )

    const handlePageChange = useCallback(
        (page: number) => {
            dispatch(fetchTrustCheckReport({ filters, page, sorting }))
        },
        [dispatch, filters, sorting],
    )

    const handleSortChange = useCallback(
        (field: string, direction: 'ASC' | 'DESC') => {
            dispatch(setSorting({ field, direction }))
            dispatch(fetchTrustCheckReport({ filters, page: 0, sorting: { field, direction } }))
        },
        [dispatch, filters],
    )

    return (
        <ReportList
            pages={pages}
            rows={rows as unknown as Record<string, ReportData>}
            isLoading={isLoading}
            pagination={pagination}
            filters={filters}
            fetchData={handlePageChange}
            columns={columns}
            renderRow={renderRow}
            sorting={sorting}
            onSortChange={handleSortChange}
            emptyMessage="No providers match the current filters (or no run has completed yet)."
            loadingMessage="Loading trust check results..."
        />
    )
}

export default TrustCheckReportList
